const SHORTCODE_REGEX = /\[products\s+([^\]]+)\]/gi;
const SHORTCODE_DETECTION_REGEX = /\[products\b/i;
const IDS_ATTRIBUTE_REGEX = /\bids\s*=\s*(?:(['"])(.*?)\1|([^\s\]]+))/i;
const HANDLES_ATTRIBUTE_REGEX = /\bhandles?\s*=\s*(?:(['"])(.*?)\1|([^\s\]]+))/i;
const PRODUCTS_PAGE_SIZE = 250;
const MAX_PRODUCTS_JSON_PAGES = 24;
const SHORTCODE_ROOT_SELECTORS = [
  '.blog-post-content rte-formatter',
  '.blog-post-content',
  'rte-formatter',
];
const RETRY_DELAYS_MS = [0, 250, 1000, 2500, 5000];
const OBSERVER_LIFETIME_MS = 20000;

/** @type {Map<string, Promise<HTMLElement | null>>} */
const productCardCache = new Map();
/** @type {Map<string, Promise<ProductJson | null>>} */
const productJsonCache = new Map();
/** @type {boolean} */
let observerStarted = false;
/** @type {MutationObserver | null} */
let shortcodeObserver = null;
/** @type {number | null} */
let queuedInitTimeout = null;

/**
 * @typedef {object} ProductsShortcodeMarker
 * @property {HTMLSpanElement} marker
 * @property {string[]} ids
 * @property {string[]} handles
 */

/**
 * @typedef {object} ProductJson
 * @property {string} handle
 * @property {string} title
 * @property {number} price
 * @property {number} [compare_at_price]
 * @property {string} [featured_image]
 * @property {string[]} [images]
 */

/**
 * Parses a comma-separated list into a clean array.
 *
 * @param {string} value
 * @returns {string[]}
 */
function parseCsvList(value) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Parses a [products ...] shortcode.
 *
 * @param {string} rawShortcode
 * @returns {{ ids: string[]; handles: string[] } | null}
 */
function parseProductsShortcode(rawShortcode) {
  const normalizedShortcode = rawShortcode
    .replace(/\\u0027/gi, "'")
    .replace(/\\u0022/gi, '"')
    .replace(/&apos;|&#39;/gi, "'")
    .replace(/&quot;|&#34;/gi, '"')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"');

  const idsMatch = normalizedShortcode.match(IDS_ATTRIBUTE_REGEX);
  const handlesMatch = normalizedShortcode.match(HANDLES_ATTRIBUTE_REGEX);
  const idsValue = idsMatch ? (idsMatch[2] || idsMatch[3] || '') : '';
  const handlesValue = handlesMatch ? (handlesMatch[2] || handlesMatch[3] || '') : '';

  const ids = parseCsvList(idsValue).filter((value) => /^\d+$/.test(value));
  const handles = parseCsvList(handlesValue);

  if (!ids.length && !handles.length) {
    return null;
  }

  return { ids, handles };
}

/**
 * Returns the storefront root path (supports markets/language prefixes).
 *
 * @returns {string}
 */
function getStoreRootPath() {
  const shopify = /** @type {{ routes?: { root?: string } } | undefined} */ (window.Shopify);
  const routes = shopify && shopify.routes ? shopify.routes : null;
  const root = routes && routes.root ? routes.root : '/';
  return root.endsWith('/') ? root : `${root}/`;
}

/**
 * Fetches products from storefront products.json.
 *
 * @param {URL} url
 * @returns {Promise<Array<{ id: number; handle: string }>>}
 */
async function fetchProductsJson(url) {
  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      console.warn('[blog-shortcodes] products.json request failed', {
        url: url.toString(),
        status: response.status,
      });
      return [];
    }

    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  } catch (error) {
    console.warn('[blog-shortcodes] products.json fetch error', {
      url: url.toString(),
      error,
    });
    return [];
  }
}

/**
 * Resolves product IDs to handles.
 *
 * @param {string[]} ids
 * @returns {Promise<Map<string, string>>}
 */
async function resolveHandlesById(ids) {
  const handlesById = new Map();
  const uniqueIds = Array.from(new Set(ids));

  if (!uniqueIds.length) return handlesById;

  const rootPath = getStoreRootPath();
  const byIdsUrl = new URL(`${rootPath}products.json`, window.location.origin);
  byIdsUrl.searchParams.set('limit', String(Math.min(uniqueIds.length, PRODUCTS_PAGE_SIZE)));
  byIdsUrl.searchParams.set('ids', uniqueIds.join(','));

  const directProducts = await fetchProductsJson(byIdsUrl);

  directProducts.forEach((product) => {
    if (product && product.id && product.handle) {
      handlesById.set(String(product.id), product.handle);
    }
  });

  const unresolvedIds = uniqueIds.filter((id) => !handlesById.has(id));
  if (!unresolvedIds.length) return handlesById;

  const unresolvedSet = new Set(unresolvedIds);
  let previousLastId = '';

  for (let page = 1; page <= MAX_PRODUCTS_JSON_PAGES && unresolvedSet.size > 0; page++) {
    const productsPageUrl = new URL(`${rootPath}products.json`, window.location.origin);
    productsPageUrl.searchParams.set('limit', String(PRODUCTS_PAGE_SIZE));
    productsPageUrl.searchParams.set('page', String(page));

    const products = await fetchProductsJson(productsPageUrl);
    if (!products.length) break;

    products.forEach((product) => {
      const productId = String((product && product.id) || '');

      if (unresolvedSet.has(productId) && product && product.handle) {
        handlesById.set(productId, product.handle);
        unresolvedSet.delete(productId);
      }
    });

    const lastProduct = products[products.length - 1];
    const lastId = String((lastProduct && lastProduct.id) || '');
    if (products.length < PRODUCTS_PAGE_SIZE || (lastId && lastId === previousLastId)) {
      break;
    }
    previousLastId = lastId;
  }

  return handlesById;
}

/**
 * Extracts shortcode markers from text content inside an element.
 * Supports shortcodes split across multiple text nodes/elements.
 *
 * @param {Element} root
 * @returns {ProductsShortcodeMarker[]}
 */
function extractShortcodeMarkers(root) {
  /** @type {ProductsShortcodeMarker[]} */
  const markers = [];

  root.normalize();

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  /** @type {Array<{ node: Text; start: number; end: number }>} */
  const textNodeOffsets = [];
  let fullText = '';

  while (walker.nextNode()) {
    if (!(walker.currentNode && walker.currentNode.nodeType === Node.TEXT_NODE)) {
      continue;
    }
    const textNode = /** @type {Text} */ (walker.currentNode);
    const parentElement = textNode.parentElement;
    if (
      parentElement &&
      ['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE'].includes(parentElement.tagName)
    ) {
      continue;
    }

    const value = textNode.textContent || '';
    if (!value) continue;

    const start = fullText.length;
    fullText += value;
    textNodeOffsets.push({ node: textNode, start, end: fullText.length });
  }

  if (!SHORTCODE_DETECTION_REGEX.test(fullText)) return markers;

  /** @type {Array<{ start: number; end: number; raw: string; parsed: { ids: string[]; handles: string[] } | null }>} */
  const matches = [];
  SHORTCODE_REGEX.lastIndex = 0;
  let match = SHORTCODE_REGEX.exec(fullText);

  while (match) {
    const rawShortcode = match[0];
    const start = match.index;
    const end = start + rawShortcode.length;
    matches.push({
      start,
      end,
      raw: rawShortcode,
      parsed: parseProductsShortcode(rawShortcode),
    });
    match = SHORTCODE_REGEX.exec(fullText);
  }

  if (!matches.length) return markers;

  for (let index = matches.length - 1; index >= 0; index--) {
    const current = matches[index];
    const startEntry = textNodeOffsets.find(
      (entry) => current.start >= entry.start && current.start < entry.end
    );
    const endLookupOffset = Math.max(current.start, current.end - 1);
    const endEntry = textNodeOffsets.find(
      (entry) => endLookupOffset >= entry.start && endLookupOffset < entry.end
    );

    if (!startEntry || !endEntry) continue;

    const range = document.createRange();
    range.setStart(startEntry.node, current.start - startEntry.start);
    range.setEnd(endEntry.node, current.end - endEntry.start);

    if (current.parsed) {
      const marker = document.createElement('span');
      marker.className = 'blog-post-products-shortcode-marker';
      marker.hidden = true;
      markers.push({
        marker,
        ids: current.parsed.ids,
        handles: current.parsed.handles,
      });
      range.deleteContents();
      range.insertNode(marker);
    }

    range.detach();
  }

  return markers;
}

/**
 * Fetches a rendered product card from the section rendering endpoint.
 *
 * @param {string} handle
 * @returns {Promise<HTMLElement | null>}
 */
async function fetchRenderedProductCard(handle) {
  const rootPath = getStoreRootPath();
  const sectionUrl = new URL(`${rootPath}products/${encodeURIComponent(handle)}`, window.location.origin);
  sectionUrl.searchParams.set('section_id', 'section-rendering-blog-product-card');

  try {
    const response = await fetch(sectionUrl.toString(), {
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
      console.warn('[blog-shortcodes] section render request failed', {
        handle,
        url: sectionUrl.toString(),
        status: response.status,
      });
      return await fetchFallbackProductCard(handle);
    }

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const card = parsed.querySelector('.blog-shortcode-product-card');

    if (card instanceof HTMLElement) {
      return card;
    }

    console.warn('[blog-shortcodes] section render response missing card markup', {
      handle,
      url: sectionUrl.toString(),
    });

    return await fetchFallbackProductCard(handle);
  } catch (error) {
    console.warn('[blog-shortcodes] section render fetch error', {
      handle,
      url: sectionUrl.toString(),
      error,
    });
    return await fetchFallbackProductCard(handle);
  }
}

/**
 * Gets active storefront currency code.
 *
 * @returns {string}
 */
function getStoreCurrencyCode() {
  const shopify = /** @type {{ currency?: { active?: string } } | undefined } */ (window.Shopify);
  const codeFromShopify =
    shopify && shopify.currency && typeof shopify.currency.active === 'string'
      ? shopify.currency.active
      : '';
  if (codeFromShopify) return codeFromShopify;

  const ogCurrencyMeta = document.querySelector('meta[property="og:price:currency"]');
  const codeFromMeta =
    ogCurrencyMeta && typeof ogCurrencyMeta.getAttribute('content') === 'string'
      ? ogCurrencyMeta.getAttribute('content')
      : '';

  return codeFromMeta || 'EUR';
}

/**
 * Formats cents in storefront currency.
 *
 * @param {number} cents
 * @returns {string}
 */
function formatPrice(cents) {
  const safeCents = Number.isFinite(cents) ? cents : 0;
  const amount = safeCents / 100;
  const currency = getStoreCurrencyCode();
  const locale = document.documentElement.lang || undefined;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/**
 * Builds a fallback product card from product JSON.
 *
 * @param {ProductJson} product
 * @returns {HTMLElement}
 */
function buildFallbackProductCard(product) {
  const rootPath = getStoreRootPath();
  const href = `${rootPath}products/${encodeURIComponent(product.handle)}`;
  const imageUrl =
    product.featured_image ||
    (Array.isArray(product.images) && product.images.length ? product.images[0] : '');

  const article = document.createElement('article');
  article.className = 'blog-shortcode-product-card product-card';
  article.setAttribute('aria-label', product.title || '');

  const link = document.createElement('a');
  link.className = 'blog-shortcode-product-card__link product-card__link';
  link.href = href;
  article.append(link);

  const linkText = document.createElement('span');
  linkText.className = 'visually-hidden';
  linkText.textContent = product.title || '';
  link.append(linkText);

  const content = document.createElement('div');
  content.className = 'blog-shortcode-product-card__content product-grid__card';
  article.append(content);

  const media = document.createElement('div');
  media.className = 'blog-shortcode-product-card__media card-gallery';
  content.append(media);

  if (imageUrl) {
    const image = document.createElement('img');
    image.className = 'blog-shortcode-product-card__image';
    image.src = imageUrl;
    image.alt = product.title || '';
    image.loading = 'lazy';
    media.append(image);
  } else {
    const placeholder = document.createElement('div');
    placeholder.className = 'blog-shortcode-product-card__image blog-shortcode-product-card__image--placeholder';
    media.append(placeholder);
  }

  const ledger = document.createElement('div');
  ledger.className = 'collection-ledger';
  content.append(ledger);

  const title = document.createElement('h3');
  title.className = 'collection-ledger__heading';

  const titleText = document.createElement('span');
  titleText.className = 'collection-ledger__title';
  titleText.textContent = product.title || '';
  title.append(titleText);
  ledger.append(title);

  const meta = document.createElement('div');
  meta.className = 'collection-ledger__meta';
  ledger.append(meta);

  const price = document.createElement('div');
  price.className = 'collection-ledger__price';
  price.textContent = formatPrice(product.price);
  meta.append(price);

  return article;
}

/**
 * Fetches product JSON by handle with cache.
 *
 * @param {string} handle
 * @returns {Promise<ProductJson | null>}
 */
function getProductJsonByHandle(handle) {
  const normalizedHandle = handle.trim();
  if (!normalizedHandle) return Promise.resolve(null);

  if (!productJsonCache.has(normalizedHandle)) {
    productJsonCache.set(
      normalizedHandle,
      (async () => {
        const rootPath = getStoreRootPath();
        const productUrl = new URL(
          `${rootPath}products/${encodeURIComponent(normalizedHandle)}.js`,
          window.location.origin
        );

        try {
          const response = await fetch(productUrl.toString(), {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) {
            console.warn('[blog-shortcodes] fallback product JSON request failed', {
              handle: normalizedHandle,
              url: productUrl.toString(),
              status: response.status,
            });
            return null;
          }

          const product = await response.json();
          if (!product || typeof product !== 'object') return null;

          return /** @type {ProductJson} */ (product);
        } catch (error) {
          console.warn('[blog-shortcodes] fallback product JSON fetch error', {
            handle: normalizedHandle,
            url: productUrl.toString(),
            error,
          });
          return null;
        }
      })()
    );
  }

  return productJsonCache.get(normalizedHandle) || Promise.resolve(null);
}

/**
 * Builds fallback card from product JSON endpoint.
 *
 * @param {string} handle
 * @returns {Promise<HTMLElement | null>}
 */
async function fetchFallbackProductCard(handle) {
  const product = await getProductJsonByHandle(handle);
  if (!product || !product.handle) return null;
  return buildFallbackProductCard(product);
}

/**
 * Returns a cached rendered card element for a product handle.
 *
 * @param {string} handle
 * @returns {Promise<HTMLElement | null>}
 */
function getRenderedProductCard(handle) {
  const normalizedHandle = handle.trim();

  if (!normalizedHandle) {
    return Promise.resolve(null);
  }

  if (!productCardCache.has(normalizedHandle)) {
    productCardCache.set(normalizedHandle, fetchRenderedProductCard(normalizedHandle));
  }

  return productCardCache.get(normalizedHandle) || Promise.resolve(null);
}

/**
 * Replaces marker with a node, optionally replacing the surrounding paragraph if marker is the only meaningful content.
 *
 * @param {HTMLSpanElement} marker
 * @param {Node} replacement
 * @param {boolean} [replaceStandaloneParagraph]
 */
function replaceMarker(marker, replacement, replaceStandaloneParagraph = false) {
  const fallbackReplace = () => {
    if (marker.parentNode) {
      marker.parentNode.replaceChild(replacement, marker);
    }
  };

  if (!replaceStandaloneParagraph) {
    fallbackReplace();
    return;
  }

  const parent = marker.parentElement;
  const canReplaceParagraph =
    parent &&
    parent.tagName === 'P' &&
    Array.from(parent.childNodes).every(
      (node) =>
        node === marker ||
        (node.nodeType === Node.TEXT_NODE &&
          !((typeof node.textContent === 'string' ? node.textContent : '').trim()))
    );

  if (canReplaceParagraph && parent && parent.parentNode) {
    parent.parentNode.replaceChild(replacement, parent);
  } else {
    fallbackReplace();
  }
}

/**
 * Renders a single shortcode marker into a product grid.
 *
 * @param {ProductsShortcodeMarker} shortcode
 * @param {Map<string, string>} handlesById
 * @returns {Promise<'rendered' | 'removed'>}
 */
async function renderShortcode(shortcode, handlesById) {
  const handles = shortcode.handles.length
    ? shortcode.handles
    : shortcode.ids.map((id) => handlesById.get(id) || '').filter(Boolean);

  /** @type {string[]} */
  const uniqueHandles = [];
  /** @type {Set<string>} */
  const seenHandles = new Set();

  handles.forEach((handle) => {
    if (!seenHandles.has(handle)) {
      seenHandles.add(handle);
      uniqueHandles.push(handle);
    }
  });

  if (!uniqueHandles.length) {
    replaceMarker(shortcode.marker, document.createTextNode(''), true);
    return 'removed';
  }

  const renderedCards = await Promise.all(uniqueHandles.map((handle) => getRenderedProductCard(handle)));
  const validCards = renderedCards.filter((card) => card instanceof HTMLElement);

  if (!validCards.length) {
    replaceMarker(shortcode.marker, document.createTextNode(''), true);
    return 'removed';
  }

  const wrapper = document.createElement('div');
  wrapper.className = 'blog-post-products-shortcode';

  const list = document.createElement('ul');
  list.className = 'blog-post-products-shortcode__list product-grid product-grid--grid';
  list.setAttribute('product-grid-view', 'default');
  list.setAttribute('role', 'list');
  wrapper.append(list);

  validCards.forEach((card) => {
    const item = document.createElement('li');
    item.className = 'blog-post-products-shortcode__item product-grid__item';
    item.append(card.cloneNode(true));
    list.append(item);
  });

  replaceMarker(shortcode.marker, wrapper, true);
  return 'rendered';
}

/**
 * Finds possible roots where shortcode content can exist.
 *
 * @returns {Element[]}
 */
function findShortcodeRoots() {
  /** @type {Element[]} */
  const roots = [];
  /** @type {Set<Element>} */
  const seen = new Set();

  SHORTCODE_ROOT_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((candidate) => {
      if (seen.has(candidate)) return;

      const candidateText = candidate.textContent || '';
      if (!SHORTCODE_DETECTION_REGEX.test(candidateText)) return;

      seen.add(candidate);
      roots.push(candidate);
    });
  });

  return roots;
}

/**
 * Initializes shortcode rendering for blog post content.
 *
 * @returns {Promise<boolean>}
 */
async function initBlogProductShortcodes() {
  const roots = findShortcodeRoots();
  if (!roots.length) return false;

  /** @type {ProductsShortcodeMarker[]} */
  const shortcodeMarkers = [];

  roots.forEach((root) => {
    shortcodeMarkers.push(...extractShortcodeMarkers(root));
  });

  if (!shortcodeMarkers.length) return false;

  /** @type {string[]} */
  const allIds = [];
  shortcodeMarkers.forEach((marker) => {
    allIds.push(...marker.ids);
  });
  const handlesById = await resolveHandlesById(allIds);

  await Promise.all(shortcodeMarkers.map((shortcode) => renderShortcode(shortcode, handlesById)));
  return true;
}

/** @type {boolean} */
let initInFlight = false;

/**
 * Runs shortcode initialization with a simple in-flight guard.
 *
 * @returns {Promise<boolean>}
 */
async function runInitSafely() {
  if (initInFlight) return false;
  initInFlight = true;

  try {
    return await initBlogProductShortcodes();
  } finally {
    initInFlight = false;
  }
}

/**
 * Queues shortcode initialization so repeated triggers collapse into one run.
 */
function queueInitRun() {
  if (queuedInitTimeout !== null) return;

  queuedInitTimeout = window.setTimeout(() => {
    queuedInitTimeout = null;
    void runInitSafely();
  }, 120);
}

/**
 * Starts a temporary observer for delayed/dynamic article content.
 */
function startShortcodeObserver() {
  if (observerStarted || !(document.body instanceof HTMLElement)) return;
  observerStarted = true;

  shortcodeObserver = new MutationObserver(() => {
    queueInitRun();
  });

  shortcodeObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  window.setTimeout(() => {
    if (shortcodeObserver) {
      shortcodeObserver.disconnect();
      shortcodeObserver = null;
    }
  }, OBSERVER_LIFETIME_MS);
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      RETRY_DELAYS_MS.forEach((delay) => {
        window.setTimeout(() => {
          void runInitSafely();
        }, delay);
      });
      startShortcodeObserver();
    },
    { once: true }
  );
} else {
  RETRY_DELAYS_MS.forEach((delay) => {
    window.setTimeout(() => {
      void runInitSafely();
    }, delay);
  });
  startShortcodeObserver();
}

window.addEventListener('load', () => {
  RETRY_DELAYS_MS.forEach((delay) => {
    window.setTimeout(() => {
      void runInitSafely();
    }, delay);
  });
});

window.addEventListener('pageshow', () => {
  void runInitSafely();
});
